import { useEffect } from 'react';
import { useInvalidate } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWebSocket,
  type LivestreamRealtimeMessage,
  type LivestreamRealtimeType,
} from '@/providers/WebSocketProvider';

type PlainObject = { [key: string]: unknown };

interface RefineSingleResponse {
  data: PlainObject;
}

interface HasDataField {
  data: unknown;
}

interface HasPatchField {
  patch: unknown;
}

interface HasInteractionIdField {
  interactionId: unknown;
}

function isPlainObject(value: unknown): value is PlainObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasDataField(value: PlainObject): value is PlainObject & HasDataField {
  return 'data' in value;
}

function hasPatchField(value: PlainObject): value is PlainObject & HasPatchField {
  return 'patch' in value;
}

function hasInteractionIdField(value: PlainObject): value is PlainObject & HasInteractionIdField {
  return 'interactionId' in value;
}

function isLivestreamRealtimeMessage(value: unknown): value is LivestreamRealtimeMessage {
  if (!isPlainObject(value)) {
    return false;
  }
  return 'type' in value && 'livestreamId' in value && 'payload' in value;
}

function extractLivestreamUpdatedPatch(payload: unknown): PlainObject | undefined {
  if (!isPlainObject(payload)) {
    return undefined;
  }
  if (!hasPatchField(payload)) {
    return undefined;
  }
  if (!isPlainObject(payload.patch)) {
    return undefined;
  }
  return payload.patch;
}

function extractInteractionId(payload: unknown): number | undefined {
  if (!isPlainObject(payload)) {
    return undefined;
  }
  if (!hasInteractionIdField(payload)) {
    return undefined;
  }
  return typeof payload.interactionId === 'number' ? payload.interactionId : undefined;
}

function isRefineSingleResponse(value: unknown): value is RefineSingleResponse {
  if (!isPlainObject(value)) {
    return false;
  }
  if (!hasDataField(value)) {
    return false;
  }
  return isPlainObject(value.data);
}

/**
 * Подписка страницы трансляции в админке на push-обновления через
 * /ws/notifications. Сервер уведомляет дельтами; хук точечно
 * инвалидирует только те queryKey, на которые событие реально
 * влияет — TanStack Query сделает один сетевой запрос вместо
 * пяти независимых polling-таймеров.
 *
 * Полная карта событий → query-инвалидаций:
 * - livestream.updated — карточка трансляции (+ optimistic merge patch'а).
 * - livestream.viewers.changed — /viewers + /network-capacity.
 * - livestream.interaction.changed — /interactions, и /responses конкретного
 *   интерактива по interactionId из payload (если есть).
 * - livestream.analytics.tick — /analytics + /network-capacity (cron 4с).
 * - livestream.media.runtime — /analytics (mediaRuntime snapshot внутри ответа).
 * - livestream.obs.diagnostics — /obs-diagnostics + /raw-logs.
 * - livestream.call.changed — /admin/calls/by-livestream/<id>.
 * - livestream.breakout.changed — /admin/breakout/status/<id>.
 */
export function useLivestreamRealtime(livestreamId: number | undefined): void {
  const { lastMessage } = useWebSocket();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!lastMessage || !livestreamId) {
      return;
    }
    if (!isLivestreamRealtimeType(lastMessage.type)) {
      return;
    }
    if (!isLivestreamRealtimeMessage(lastMessage)) {
      return;
    }
    const message = lastMessage;
    if (message.livestreamId !== livestreamId) {
      return;
    }

    switch (message.type) {
      case 'livestream.updated': {
        // Optimistic merge — позволяет фронту мгновенно показать новые
        // поля (status, scheduledAt, streamKey) до прихода refetch-ответа.
        const patch = extractLivestreamUpdatedPatch(message.payload);
        if (patch) {
          queryClient.setQueriesData(
            { predicate: (query) => isLivestreamDetailQuery(query.queryKey, livestreamId) },
            (prev: unknown) => mergeLivestreamPatch(prev, patch),
          );
        }
        invalidate({
          resource: 'livestreams',
          invalidates: ['detail', 'list'],
          id: livestreamId,
        });
        break;
      }

      case 'livestream.viewers.changed': {
        queryClient.invalidateQueries({
          predicate: (query) => isLivestreamCustomQuery(query.queryKey, livestreamId, '/viewers'),
        });
        // Изменение числа зрителей меняет и сетевую нагрузку — обновляем
        // карточку ёмкости сети тем же событием.
        queryClient.invalidateQueries({
          predicate: (query) =>
            isLivestreamCustomQuery(query.queryKey, livestreamId, '/network-capacity'),
        });
        break;
      }

      case 'livestream.interaction.changed': {
        queryClient.invalidateQueries({
          predicate: (query) =>
            isLivestreamCustomQuery(query.queryKey, livestreamId, '/interactions'),
        });
        const interactionId = extractInteractionId(message.payload);
        if (typeof interactionId === 'number') {
          queryClient.invalidateQueries({
            predicate: (query) =>
              isLivestreamCustomQuery(
                query.queryKey,
                livestreamId,
                `/interactions/${interactionId}/responses`,
              ),
          });
        } else {
          queryClient.invalidateQueries({
            predicate: (query) =>
              isLivestreamCustomQuery(query.queryKey, livestreamId, '/interactions/'),
          });
        }
        break;
      }

      case 'livestream.analytics.tick': {
        queryClient.invalidateQueries({
          predicate: (query) =>
            isLivestreamCustomQuery(query.queryKey, livestreamId, '/analytics'),
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            isLivestreamCustomQuery(query.queryKey, livestreamId, '/network-capacity'),
        });
        break;
      }

      case 'livestream.media.runtime': {
        queryClient.invalidateQueries({
          predicate: (query) =>
            isLivestreamCustomQuery(query.queryKey, livestreamId, '/analytics'),
        });
        break;
      }

      case 'livestream.obs.diagnostics': {
        queryClient.invalidateQueries({
          predicate: (query) =>
            isLivestreamCustomQuery(query.queryKey, livestreamId, '/obs-diagnostics')
            || isLivestreamCustomQuery(query.queryKey, livestreamId, '/raw-logs'),
        });
        break;
      }

      case 'livestream.call.changed': {
        queryClient.invalidateQueries({
          predicate: (query) => isAdminCallByLivestreamQuery(query.queryKey, livestreamId),
        });
        break;
      }

      case 'livestream.breakout.changed': {
        queryClient.invalidateQueries({
          predicate: (query) => isBreakoutStatusQuery(query.queryKey, livestreamId),
        });
        break;
      }

      case 'livestream.source.changed': {
        // Статус источников (OBS/Zoom) и активный источник изменились —
        // обновляем карточку источника + detail (там лежит activeSource).
        queryClient.invalidateQueries({
          predicate: (query) => isLivestreamCustomQuery(query.queryKey, livestreamId, '/sources'),
        });
        invalidate({
          resource: 'livestreams',
          invalidates: ['detail'],
          id: livestreamId,
        });
        break;
      }

      case 'livestream.relay.status': {
        // Статусы relay лежат в основном DTO трансляции (relayStatuses) —
        // инвалидируем detail, чтобы панель рестрима перечиталась.
        invalidate({
          resource: 'livestreams',
          invalidates: ['detail'],
          id: livestreamId,
        });
        break;
      }
    }
  }, [lastMessage, livestreamId, invalidate, queryClient]);
}

const LIVESTREAM_REALTIME_TYPES: ReadonlySet<LivestreamRealtimeType> = new Set([
  'livestream.updated',
  'livestream.viewers.changed',
  'livestream.analytics.tick',
  'livestream.interaction.changed',
  'livestream.media.runtime',
  'livestream.breakout.changed',
  'livestream.obs.diagnostics',
  'livestream.call.changed',
  'livestream.source.changed',
  'livestream.relay.status',
]);

function isLivestreamRealtimeType(type: string): type is LivestreamRealtimeType {
  const types: ReadonlySet<string> = LIVESTREAM_REALTIME_TYPES;
  return types.has(type);
}

/**
 * Refine 5 формирует queryKey для useOne в виде
 * ['default', 'livestreams', 'one', '<id>', { meta }]. Точную форму
 * распознаём по наличию resource + 'one' + id где-то в массиве.
 */
function isLivestreamDetailQuery(queryKey: readonly unknown[], livestreamId: number): boolean {
  return (
    queryKey.includes('livestreams')
    && queryKey.includes('one')
    && queryKey.some((part) => part === String(livestreamId) || part === livestreamId)
  );
}

/**
 * useCustom формирует queryKey, в который входит url и method. Идентифицируем
 * по подстроке url.
 */
function isLivestreamCustomQuery(
  queryKey: readonly unknown[],
  livestreamId: number,
  pathSuffix: string,
): boolean {
  const expectedFragment = `/admin/livestreams/${livestreamId}${pathSuffix}`;
  return queryKey.some(
    (part) => typeof part === 'string' && part.startsWith(expectedFragment),
  );
}

/**
 * useCustom для breakout-статуса формирует queryKey с url
 * `/admin/breakout/status/<livestreamId>`.
 */
function isBreakoutStatusQuery(
  queryKey: readonly unknown[],
  livestreamId: number,
): boolean {
  const expected = `/admin/breakout/status/${livestreamId}`;
  return queryKey.some(
    (part) => typeof part === 'string' && part.startsWith(expected),
  );
}

/**
 * useCustom для активного звонка трансляции — url вида
 * `/admin/calls/by-livestream/<livestreamId>`.
 */
function isAdminCallByLivestreamQuery(
  queryKey: readonly unknown[],
  livestreamId: number,
): boolean {
  const expected = `/admin/calls/by-livestream/${livestreamId}`;
  return queryKey.some(
    (part) => typeof part === 'string' && part.startsWith(expected),
  );
}

function mergeLivestreamPatch(prev: unknown, patch: PlainObject): unknown {
  if (!isPlainObject(prev)) {
    return prev;
  }
  // Refine 5 useOne хранит в кэше { data: <entity> }.
  if (isRefineSingleResponse(prev)) {
    return {
      ...prev,
      data: { ...prev.data, ...patch },
    };
  }
  return { ...prev, ...patch };
}
